

import React, { useEffect } from 'react';
import Layout from '@/components/Layout';
import { useRecipes } from '@/contexts/RecipesContext';
import { Star, Award, Heart, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useOptimizedExploreData } from '@/hooks/useOptimizedExploreData';
import { useSentryError } from '@/hooks/useSentryError';
import ExploreSection from '@/components/ExploreSection';

const Explore = () => {
  const { recipes } = useRecipes();
  const { captureMessage } = useSentryError();
  
  // Use optimized hook for better performance
  const {
    featuredRecipes,
    newRecipes,
    popularRecipes,
    mostLikedRecipes,
    loadingStates,
    isLoadingLikes,
    totalRecipesProcessed,
    originalRecipesCount
  } = useOptimizedExploreData({ recipes, maxRecipes: 80 });

  // Log page visit and performance info
  useEffect(() => {
    captureMessage('Explore page visited', 'info', {
      component: 'Explore',
      action: 'pageVisit',
      additionalData: {
        originalRecipesCount,
        totalRecipesProcessed,
        isLoadingLikes
      }
    });

    console.log(`📊 EXPLORE PAGE PERFORMANCE:
    - Original recipes: ${originalRecipesCount}
    - Processed recipes: ${totalRecipesProcessed}
    - Loading likes: ${isLoadingLikes}
    - Loading states:`, loadingStates);
  }, [originalRecipesCount, totalRecipesProcessed, isLoadingLikes, loadingStates, captureMessage]);

  return (
    <Layout>
      <div className="py-12 bg-gradient-to-b from-white to-green-50">
        <div className="px-4">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-vegana-primary mb-4">
              Explora nuestras <span className="text-vegana-secondary">recetas</span>
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Descubre nuestra selección de recetas destacadas, las últimas novedades, 
              y las más valoradas por nuestra comunidad.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-[2000px] mx-auto"
          >
            <ExploreSection
              title="Con más Likes"
              icon={Heart}
              recipes={mostLikedRecipes}
              isLoading={loadingStates.mostLiked || isLoadingLikes}
              className="mb-16"
            />
            
            <ExploreSection
              title="Recetas Destacadas"
              icon={Star}
              recipes={featuredRecipes}
              isLoading={loadingStates.featured}
              className="mb-16"
            />
            
            <ExploreSection
              title="Últimas Recetas"
              icon={Clock}
              recipes={newRecipes}
              isLoading={loadingStates.new}
              className="mb-16"
            />
            
            <ExploreSection
              title="Más Valoradas"
              icon={Award}
              recipes={popularRecipes}
              isLoading={loadingStates.popular}
            />
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Explore;
